import { ReactNode, useMemo, useState } from 'react';

import { Box } from 'components/Box';
import { Layout } from 'layouts/Layout/Layout';
import { CmsApiPagePage } from 'modules/cms/api/types/CmsApiPage';
import { CmsRecommendedJobsBlockLoadable } from 'modules/cms/blocks/RecommendedJobs/CmsRecommendedJobsBlockLoadable';
import {
  CmsBodyHolder,
  CmsBodyHolderWidth,
} from 'modules/cms/components/BodyHolder/CmsBodyHolder';
import { SubscribedToSubsiteModalLoadable } from 'modules/cms/components/SubscribedToSubsiteModal/SubscribedToSubsiteModalLoadable';
import {
  getContentLocaleUrl,
  getHideFooterDivider,
  getIsHomePage,
} from 'modules/cms/helpers';
import {
  useCmsApiPageDefaultSearchType,
  useIsLoggedIn,
} from 'modules/cms/hooks';
import { sectionPadding } from 'theme/theme';
import { isDefined } from 'utils/functional';

type Props = {
  content: CmsApiPagePage;
  children: ReactNode;
};

export function CmsPagePageLayout({ content, children }: Props) {
  const [showSubscribedModal, setShowSubscribedModal] = useState(false);

  const defaultSearchType = useCmsApiPageDefaultSearchType({
    cmsApiPage: content,
  });
  const loggedIn = useIsLoggedIn();

  const hero = content.heroSearch?.onlyResults ? undefined : content.heroSearch;

  // banner ads should not be shown for logged out users on home page
  const noAds = !loggedIn && getIsHomePage(content);

  const hasBelowBlocks = useMemo(
    () =>
      content.hasRecommendedJobs ||
      (content.tags && content.tags.length > 0) ||
      content.author ||
      content.hasReactions ||
      content.enableComments,
    [
      content.author,
      content.enableComments,
      content.hasReactions,
      content.hasRecommendedJobs,
      content.tags,
    ],
  );

  const hideFooterDivider = getHideFooterDivider({ content });
  const localeUrl = getContentLocaleUrl({ content });

  return (
    <>
      <Layout
        content={content}
        hero={hero}
        noAds={noAds}
        bottomTableOfContents={content.tableOfContents}
        defaultWithSearchHero={isDefined(content.heroSearch)}
        defaultSearchType={defaultSearchType}
        hideFooterDivider={hideFooterDivider}
        urlByLocale={localeUrl}
        showDesktopSearch={!hero}
        preFooter={
          hasBelowBlocks && (
            <Box
              flex="1 1 auto"
              width="100%"
              m={sectionPadding}
              mt={[16, null, 24]}
            >
              {content.hasRecommendedJobs && (
                <CmsBodyHolder $width={CmsBodyHolderWidth.NARROW_LAYOUT_WIDE}>
                  <CmsRecommendedJobsBlockLoadable
                    data={{
                      headline: content.recommendedJobsHeadline || null,
                      layout: 'REGULAR',
                      jobFunction: null,
                      areaOfFocus: null,
                    }}
                  />
                </CmsBodyHolder>
              )}
            </Box>
          )
        }
      >
        {children}
      </Layout>

      {showSubscribedModal && (
        <SubscribedToSubsiteModalLoadable
          onClose={() => setShowSubscribedModal(false)}
        />
      )}
    </>
  );
}
